import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  Radar, Bot, Film, Rocket, Sparkles, TrendingUp, ArrowUpRight, Activity,
  CircleDot, Zap
} from "lucide-react";
import { motion } from "framer-motion";

const StatCell = ({ label, value, sub, accent = false, testid }) => (
  <div data-testid={testid} className={`p-6 border border-[#171717] bg-[#0a0a0a] hover:bg-[#0d0d0d] transition-colors duration-200 ${accent ? "border-l-2 !border-l-[#F5A524]" : ""}`}>
    <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mb-3">{label}</div>
    <div className="font-display font-bold text-3xl mb-1">{value}</div>
    <div className="font-mono text-[10px] uppercase text-[#A1A1AA]">{sub}</div>
  </div>
);

const CmdRow = ({ label, right, cn = "" }) => (
  <div className={`flex items-center justify-between py-2.5 border-b border-[#141414] last:border-0 ${cn}`}>
    <div className="text-sm text-[#A1A1AA]">{label}</div>
    <div className="font-mono text-[11px] text-white">{right}</div>
  </div>
);

export default function CommandCenter() {
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/analytics/overview").then(r => r.data) });
  const { data: agents } = useQuery({ queryKey: ["agents"], queryFn: () => api.get("/agents").then(r => r.data) });
  const { data: workflows } = useQuery({ queryKey: ["workflows"], queryFn: () => api.get("/studio/workflows").then(r => r.data) });
  const { data: trends } = useQuery({ queryKey: ["trends"], queryFn: () => api.get("/intelligence/trends").then(r => r.data) });
  const { data: content } = useQuery({ queryKey: ["top-content"], queryFn: () => api.get("/analytics/top-content").then(r => r.data) });

  const activeAgents = (agents || []).filter(a => a.status === "active");
  const teamCounts = (agents || []).reduce((acc, a) => { acc[a.team] = (acc[a.team] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <div className="border-b border-[#141414] px-8 py-5 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Command Center</div>
          <h1 className="font-display font-bold text-2xl">Live operations overview</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[#222] rounded-sm bg-[#0a0a0a]">
            <span className="w-1.5 h-1.5 bg-[#17C964] rounded-full" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">All systems operational</span>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* KPI bento */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-[#171717] border border-[#171717]">
          <StatCell testid="kpi-agents" label="Agents online" value={`${overview?.agents_active ?? 0}/${overview?.agents_total ?? 0}`} sub={`across ${Object.keys(teamCounts).length} teams`} accent />
          <StatCell testid="kpi-workflows" label="Workflows active" value={overview?.workflows_active ?? 0} sub="durable graphs" />
          <StatCell testid="kpi-content" label="Content pieces" value={overview?.content_total ?? 0} sub="all statuses" />
          <StatCell testid="kpi-brands" label="Brands" value={overview?.brands ?? 0} sub={`${overview?.campaigns_active ?? 0} campaigns`} />
          <StatCell testid="kpi-impressions" label="Impressions (30d)" value={(overview?.total_impressions ?? 0).toLocaleString()} sub={`${(overview?.total_reach ?? 0).toLocaleString()} reach`} />
          <StatCell testid="kpi-trends" label="Trends tracked" value={overview?.trends ?? 0} sub={`${overview?.influencers ?? 0} influencers`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Active agent stream */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-5 border border-[#171717] bg-[#0a0a0a]">
            <div className="px-5 py-4 border-b border-[#171717] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                <div>
                  <div className="font-display font-bold text-sm">Agent stream</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Live · realtime</div>
                </div>
              </div>
              <Link to="/app/agents" data-testid="agents-view-all" className="font-mono text-[10px] uppercase tracking-widest text-[#F5A524] hover:underline">View all →</Link>
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {(activeAgents.slice(0, 12)).map(a => (
                <div key={a.id} className="px-5 py-3 border-b border-[#141414] last:border-0 hover:bg-[#0f0f0f] transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-1.5 h-1.5 bg-[#F5A524] rounded-full pulse-amber flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{a.name}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] truncate">{a.team}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[10px] text-[#00F0FF]">{a.avg_latency_ms}ms</div>
                      <div className="font-mono text-[9px] text-[#52525B]">{(a.success_rate * 100).toFixed(1)}% ok</div>
                    </div>
                  </div>
                </div>
              ))}
              {activeAgents.length === 0 && (
                <div className="p-8 text-center text-sm text-[#52525B]">No agents currently active</div>
              )}
            </div>
          </motion.div>

          {/* Trend radar preview */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-4 border border-[#171717] bg-[#0a0a0a]">
            <div className="px-5 py-4 border-b border-[#171717] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
                <div>
                  <div className="font-display font-bold text-sm">Top opportunities</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Highest virality score</div>
                </div>
              </div>
              <Link to="/app/intelligence" data-testid="trends-view-all" className="font-mono text-[10px] uppercase tracking-widest text-[#F5A524] hover:underline">Intelligence →</Link>
            </div>
            <div className="p-5 space-y-3 max-h-[380px] overflow-y-auto">
              {(trends || []).slice(0, 5).map(t => (
                <div key={t.id} className="p-3 border border-[#171717] hover:border-[#2a2a2a] transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate font-medium">{t.title}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mt-0.5">{t.category} · {t.region}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] text-[#52525B]">SCORE</div>
                      <div className="font-display font-bold text-[#F5A524] text-lg leading-none">{Math.round(t.virality_score)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    {[
                      { l: "vel", v: t.velocity, c: "#F5A524" },
                      { l: "fit", v: t.brand_fit, c: "#17C964" },
                      { l: "long", v: t.longevity, c: "#00F0FF" },
                      { l: "risk", v: t.risk, c: "#F31260" },
                    ].map(m => (
                      <div key={m.l}>
                        <div className="h-1 bg-[#141414] overflow-hidden">
                          <div style={{ width: `${m.v}%`, background: m.c }} className="h-full" />
                        </div>
                        <div className="font-mono text-[8px] uppercase text-[#52525B] mt-1">{m.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(!trends || trends.length === 0) && (
                <div className="p-6 text-center text-sm text-[#52525B]">
                  No trends yet. <Link to="/app/intelligence" className="text-[#F5A524] hover:underline">Scan the culture →</Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Quick actions + Top content */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-3 space-y-6">
            <div className="border border-[#171717] bg-[#0a0a0a]">
              <div className="px-5 py-4 border-b border-[#171717]">
                <div className="font-display font-bold text-sm">Quick launch</div>
              </div>
              <div className="p-5 space-y-2">
                <Link to="/app/intelligence" data-testid="quick-scan" className="flex items-center gap-3 p-3 border border-[#171717] hover:border-[#F5A524] hover:bg-[#0f0f0f] group transition-colors">
                  <Radar className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                  <div className="text-sm flex-1">Scan trends</div>
                  <ArrowUpRight className="w-3 h-3 text-[#52525B] group-hover:text-[#F5A524]" />
                </Link>
                <Link to="/app/studio" data-testid="quick-generate" className="flex items-center gap-3 p-3 border border-[#171717] hover:border-[#F5A524] hover:bg-[#0f0f0f] group transition-colors">
                  <Film className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                  <div className="text-sm flex-1">Generate content</div>
                  <ArrowUpRight className="w-3 h-3 text-[#52525B] group-hover:text-[#F5A524]" />
                </Link>
                <Link to="/app/influencers" data-testid="quick-influencer" className="flex items-center gap-3 p-3 border border-[#171717] hover:border-[#F5A524] hover:bg-[#0f0f0f] group transition-colors">
                  <Sparkles className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                  <div className="text-sm flex-1">Cast an influencer</div>
                  <ArrowUpRight className="w-3 h-3 text-[#52525B] group-hover:text-[#F5A524]" />
                </Link>
                <Link to="/app/campaigns" data-testid="quick-brand" className="flex items-center gap-3 p-3 border border-[#171717] hover:border-[#F5A524] hover:bg-[#0f0f0f] group transition-colors">
                  <Rocket className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
                  <div className="text-sm flex-1">Add a brand</div>
                  <ArrowUpRight className="w-3 h-3 text-[#52525B] group-hover:text-[#F5A524]" />
                </Link>
              </div>
            </div>

            <div className="border border-[#171717] bg-[#0a0a0a]">
              <div className="px-5 py-4 border-b border-[#171717]">
                <div className="font-display font-bold text-sm">Recent workflows</div>
              </div>
              <div className="p-5 space-y-2">
                {(workflows || []).slice(0, 4).map(w => (
                  <div key={w.id} className="p-2.5 border border-[#171717]">
                    <div className="flex items-center gap-2 mb-1">
                      <CircleDot className={`w-3 h-3 ${w.status === "running" ? "text-[#F5A524]" : w.status === "completed" ? "text-[#17C964]" : "text-[#F31260]"}`} />
                      <span className="text-xs">{w.template}</span>
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">{w.status} · {w.current_node}</div>
                  </div>
                ))}
                {(!workflows || workflows.length === 0) && (
                  <div className="text-xs text-[#52525B]">No workflows yet.</div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Top content strip */}
        <div className="border border-[#171717] bg-[#0a0a0a]">
          <div className="px-5 py-4 border-b border-[#171717] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#F5A524]" strokeWidth={1.5} />
              <div>
                <div className="font-display font-bold text-sm">Highest virality — recent content</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Ranked by composite score</div>
              </div>
            </div>
            <Link to="/app/studio" data-testid="content-view-all" className="font-mono text-[10px] uppercase tracking-widest text-[#F5A524] hover:underline">Studio →</Link>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {(content || []).slice(0, 5).map(c => (
              <Link key={c.id} to={`/app/studio?id=${c.id}`} className="border border-[#171717] hover:border-[#2a2a2a] bg-[#080808] group transition-colors">
                <div className="aspect-video bg-[#0f0f0f] relative overflow-hidden">
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url.startsWith("http") ? c.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${c.thumbnail_url}`} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#52525B]"><Film className="w-6 h-6" /></div>
                  )}
                  <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 border border-[#222] font-mono text-[9px] uppercase text-[#F5A524]">{Math.round(c.virality_score)}</div>
                </div>
                <div className="p-3">
                  <div className="text-xs font-medium truncate">{c.title}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mt-1">{c.platform} · {c.status}</div>
                </div>
              </Link>
            ))}
            {(!content || content.length === 0) && (
              <div className="col-span-5 p-6 text-center text-sm text-[#52525B]">
                No content yet. <Link to="/app/studio" className="text-[#F5A524] hover:underline">Generate your first piece →</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
